import React, {useEffect, useState} from "react";
import {Advert} from "../types/Advert";
import {formatCurrency, formatNumber} from "../utils/numberFormat";
import {range} from "../utils/range";

type AdvertCardProps = {
    advert: Advert;
}

const AdvertCard: React.FC<AdvertCardProps> = ({advert}) => {
    const distance = advert._geoDistance ? Math.round(advert._geoDistance / 1609.34 * 10) / 10 : 0;
    const [loggedIn] = useState((window as any).loggedIn);

    useEffect(() => {
        (window as any)[`advert-${advert.id}`] = {
            id: advert.id,
            make: advert.make.name,
            model: advert.model.name,
            variant: advert.variant.name,
            image: advert.image.smallMedium,
            price: advert.price,
            mileage: advert.mileage,
            year: advert.year,
            lowestMonthlyFinanceFrom: advert.lowestMonthlyFinanceFrom,
        };

        return () => {
            delete (window as any)[`advert-${advert.id}`];
        }
    }, [advert]);

    return (
        <a href={`/vehicle/${advert.id}`} className="vehicle-card mx-n2 mx-md-0">
            <div className="vehicle-card-img position-relative splide" style={{visibility: "visible"}}
                 data-advert-id={advert.id} data-image-count={advert.imagesCount}>
                <div className="splide__track">
                    <ul className="splide__list mb-0 pl-0">
                        <li className="splide__slide" style={{minWidth: "100%"}}>
                            <img src={advert.image.medium}
                                 loading="lazy"
                                 alt={advert.make.name + " " + advert.model.name}
                                 onError={function ({currentTarget}) {
                                     currentTarget.onerror = null;
                                     currentTarget.src = "https://assets.choosemycar.com/coming-soon.png";
                                 }}/>
                        </li>
                        {advert.imagesCount > 1 && range(1, advert.imagesCount - 2).map(() => (
                            <li className="splide__slide" style={{minWidth: "100%"}}>
                                <div className="advert-image-wrapper">
                                    <img src="" alt="" loading="lazy" onLoad={function ({currentTarget}) {
                                        currentTarget.classList.add("loaded")
                                    }}/>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {advert.imagesCount > 1 &&
                    <div className="prev-next-btns d-flex justify-content-between d-none d-md-flex">
                        <button className="splide-prev">
                            <svg style={{fill: "none"}} width="26" height="46" viewBox="0 0 26 46" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 43L23 23L3 3" stroke-width="5" stroke-linecap="round"
                                      stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <button className="splide-next">
                            <svg style={{fill: "none"}} width="26" height="46" viewBox="0 0 26 46" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 43L23 23L3 3" stroke-width="5" stroke-linecap="round"
                                      stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                }

                <div className="splide_pagination_wrapper d-flex tmp_pagination">
                    <ul className="slider-nav-dots d-flex justify-content-center">
                        {advert.imagesCount > 1 && range(1, advert.imagesCount - 1).map((i) => (
                            <li>
                                <button className={`${i === 0 ? "is-active" : ""}`}></button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="vehicle-info">
                <div className="w-100 d-flex justify-content-between align-items-start gap-3">
                    <h4 className="vehicle-name mb-1">
                        <span>{advert.variant.name}</span> {advert.make.name + " " + advert.model.name}</h4>
                    <button
                        className="advert-card-add-to-wishlist-btn ga_add_to_wish_list"
                        data-advert-id={advert.id}
                        data-bs-toggle="modal"
                        data-is-wished="false"
                        data-bs-target="#wishListDialog">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                             stroke="currentColor"
                             style={{width: "1.5rem", height: "1.5rem", pointerEvents: "none"}}>
                            <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"></path>
                        </svg>
                    </button>
                </div>

                <div className="mt-2 d-flex gap-1 vehicle-specs">
                    <span className="d-inline-block">{formatNumber(advert.mileage)} miles</span>
                    <span className="d-inline-block">{advert.year}</span>
                    <span className="d-inline-block"
                          style={{fontSize: "12px", backgroundColor: "rgba(0, 182, 122, 0.1)"}}>Zero Deposit</span>
                </div>
            </div>

            <div className="d-flex flex-column" style={{padding: "0 .75rem"}}>
                <p className="mb-1 mt-1" style={{color: "#215869", fontSize: "12px"}}>Home Delivery Available</p>
                <div className="prices">
                    <dl className="cash-price">
                        <dd className="price text-primary lh-1">{formatCurrency(advert.price)}
                            <div
                                className={["d-flex align-items-center gap-1", !advert.location ? "d-none" : ""].join(" ")}
                                style={{marginTop: ".5rem"}}>
                                <svg width="11" height="15" viewBox="0 0 11 15" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M5.5081 14.1667C5.35254 14.1667 5.21921 14.1223 5.1081 14.0334C4.99699 13.9445 4.91365 13.8278 4.8581 13.6834C4.64699 13.0612 4.38032 12.4778 4.0581 11.9334C3.74699 11.3889 3.3081 10.75 2.74143 10.0167C2.17476 9.28337 1.71365 8.58337 1.3581 7.91671C1.01365 7.25004 0.841431 6.44448 0.841431 5.50004C0.841431 4.20004 1.29143 3.10004 2.19143 2.20004C3.10254 1.28893 4.2081 0.833374 5.5081 0.833374C6.8081 0.833374 7.9081 1.28893 8.8081 2.20004C9.71921 3.10004 10.1748 4.20004 10.1748 5.50004C10.1748 6.51115 9.98032 7.3556 9.59143 8.03337C9.21365 8.70004 8.77476 9.36115 8.27476 10.0167C7.67476 10.8167 7.21921 11.4834 6.9081 12.0167C6.6081 12.5389 6.3581 13.0945 6.1581 13.6834C6.10254 13.8389 6.01365 13.9612 5.89143 14.05C5.78032 14.1278 5.65254 14.1667 5.5081 14.1667ZM5.5081 7.16671C5.97476 7.16671 6.36921 7.0056 6.69143 6.68337C7.01365 6.36115 7.17476 5.96671 7.17476 5.50004C7.17476 5.03337 7.01365 4.63893 6.69143 4.31671C6.36921 3.99448 5.97476 3.83337 5.5081 3.83337C5.04143 3.83337 4.64699 3.99448 4.32476 4.31671C4.00254 4.63893 3.84143 5.03337 3.84143 5.50004C3.84143 5.96671 4.00254 6.36115 4.32476 6.68337C4.64699 7.0056 5.04143 7.16671 5.5081 7.16671Z"
                                        fill="#022935"/>
                                </svg>
                                <span>{advert.location?.name} {!!advert._geoDistance &&
                                    <small>{distance} mile{distance > 1 && "s"} away</small>}</span>
                            </div>
                        </dd>
                    </dl>
                    <dl className="finance-price align-items-end">
                        <dd className="price lh-1" style={{fontWeight: 600}}>
                            {formatCurrency(Math.round(advert.lowestMonthlyFinanceFrom.pence / 100))}<span>/month</span>
                        </dd>
                        <div className="d-inline-flex gap-1 align-items-center" data-show-representative-example="true"
                             style={{marginTop: ".375rem"}}>
                            <span className="text-decoration-underline"
                                  style={{fontSize: "10px", pointerEvents: "none"}}> Example</span>
                            <svg width="11" height="11" viewBox="0 0 9 9" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M8.60104 4.66501C8.59904 6.86627 6.82225 8.66535 4.59937 8.66401C2.39143 8.66267 0.602603 6.88053 0.603272 4.66301C0.60394 2.4428 2.40035 0.666685 4.60271 0.666016C6.80999 0.665347 8.60104 2.45774 8.60104 4.66501ZM5.29292 5.54092C5.29292 5.04801 5.29225 4.556 5.29359 4.06309C5.29426 3.87672 5.22782 3.71866 5.09027 3.59604C4.87336 3.40209 4.62122 3.36152 4.35058 3.45693C4.09242 3.54856 3.91942 3.79668 3.92076 4.07736C3.92633 5.05894 3.92366 6.04052 3.9221 7.0221C3.9221 7.57676 4.39739 7.67017 4.62456 7.6695C4.9661 7.66816 5.30184 7.39284 5.29493 6.9978C5.28623 6.51225 5.29292 6.02625 5.29292 5.54092ZM5.33974 2.3913C5.33907 1.982 5.01224 1.65584 4.60205 1.65584C4.1914 1.65584 3.86324 1.98467 3.86391 2.39532C3.86458 2.8015 4.19608 3.13234 4.60271 3.13234C5.01224 3.13234 5.33974 2.80284 5.33974 2.3913Z"
                                    fill="#022935"/>
                            </svg>
                        </div>
                    </dl>
                </div>
            </div>

            {(loggedIn &&
                advert.dealer.isApproved) ? <div className="approved-dealer ms-3 mb-2">Approved dealer</div> : ""
            }
        </a>
    )
}

export default AdvertCard;